/**
 * Workflow di approvazione delle assenze — risoluzione client-side.
 *
 * Contratto BE `BE-DONE-LEAVE-APPROVAL-WORKFLOW-CONFIG-v1` (v1.76.0, live):
 * due assi nullable con ereditarietà.
 *
 *   effectiveWorkflow =
 *     leaveType.approvalWorkflow                         se non null
 *     altrimenti il primo non-null risalendo tenant → parentTenant → …
 *     altrimenti 'two-level'
 *
 * Il fallback è **sempre** `two-level`, mai `auto`: una svista di configurazione non
 * può trasformarsi in «approva tutto». Un valore fuori vocabolario non viene
 * interpretato — si scende al livello successivo, esattamente come fa il server.
 *
 * Serve al FE perché l'etichetta del pulsante del manager cambia: in `manager-only`
 * quel click **chiude** la richiesta, non la passa all'HR. Il server rimanda anche
 * `meta.effectiveWorkflow` sulla risposta di transizione, che è la verità quando la
 * configurazione è cambiata dopo il caricamento della pagina.
 */

export const APPROVAL_WORKFLOWS = ['manager-only', 'two-level', 'auto'] as const;
export type ApprovalWorkflow = (typeof APPROVAL_WORKFLOWS)[number];

/** Valori ammessi sul tenant: `auto` è rifiutato dal BE come default aziendale. */
export const TENANT_APPROVAL_WORKFLOWS = ['manager-only', 'two-level'] as const;

export const APPROVAL_WORKFLOW_LABEL: Record<string, string> = {
  'manager-only': 'Solo responsabile',
  'two-level': 'Responsabile + HR',
  auto: 'Nessuna approvazione',
};

/** Etichetta dell'opzione «non deciso qui»: il valore sul wire è `null`. */
export const INHERIT_LABEL = 'Eredita';

/** Catena di tenant: `parentTenant` denormalizzato dall'`include`. */
export interface TenantChainNode {
  id?: string;
  leaveDefaultApprovalWorkflow?: string | null;
  parentTenant?: TenantChainNode | null;
}

const isWorkflow = (v: unknown): v is ApprovalWorkflow =>
  typeof v === 'string' && (APPROVAL_WORKFLOWS as readonly string[]).includes(v);

/**
 * Workflow effettivo. `leaveTypeWorkflow` cortocircuita l'intera catena; poi si risale
 * i tenant. La guardia anti-ciclo replica quella del server: una catena corrotta
 * (`parentTenant` che punta a sé) non deve appendere il render.
 */
export function resolveApprovalWorkflow(
  leaveTypeWorkflow: string | null | undefined,
  tenant: TenantChainNode | null | undefined,
): ApprovalWorkflow {
  if (isWorkflow(leaveTypeWorkflow)) return leaveTypeWorkflow;

  const seen = new Set<string>();
  let node: TenantChainNode | null | undefined = tenant;
  while (node) {
    // `auto` sul tenant non è ammesso dal BE: se ci finisse a mano non va interpretato.
    const v = node.leaveDefaultApprovalWorkflow;
    if (v === 'manager-only' || v === 'two-level') return v;
    if (node.id) {
      if (seen.has(node.id)) break;
      seen.add(node.id);
    }
    node = node.parentTenant;
  }
  return 'two-level';
}

/** true se dopo il passaggio del responsabile serve ancora l'HR. */
export const needsAdminApproval = (w: ApprovalWorkflow): boolean => w === 'two-level';

/**
 * Etichetta dell'azione del responsabile. In `manager-only` (e in `auto`, se una
 * richiesta è comunque in `submitted` perché il tipo è stato riconfigurato dopo il
 * submit) il click **finalizza**: chiamarlo «Approva come responsabile» farebbe
 * credere che serva ancora un passaggio.
 */
export function managerActionLabel(w: ApprovalWorkflow): string {
  return needsAdminApproval(w) ? 'Approva come responsabile' : 'Approva e chiudi';
}

/** Descrizione del passaggio successivo, per il testo di conferma. */
export function managerActionHint(w: ApprovalWorkflow): string {
  return needsAdminApproval(w)
    ? 'Passerà all’HR per l’approvazione definitiva.'
    : 'La richiesta viene approvata in via definitiva: non è previsto un passaggio HR.';
}

// ───────────────────── Le due strade di auto-approvazione preesistenti ─────────────────────
// `approvalWorkflow` decide quanti umani servono DOPO il submit, ma il submit ha già due
// strade che lo precedono. Sul catalogo CCNL sono attive su 8 tipi su 12, dove impostare
// un workflow è **inerte**: nessun errore, semplicemente non ha effetto
// (BE-CLOSURE-TO-FE-LEAVE-APPROVAL-WORKFLOW-CONFIG §2). Va detto a chi configura.

export interface AutoApproveShape {
  approvalWorkflow?: string | null;
  autoApprovePolicy?: string | null;
  isRejectable?: boolean;
  requiresDocument?: string | null;
}

export type AutoApproveRoute =
  /** `autoApprovePolicy='always'`: si auto-approva sempre. */
  | 'policy-always'
  /** `with-document`/`with-substitute`: si auto-approva quando la condizione è soddisfatta. */
  | 'policy-conditional'
  /** `isRejectable=false` + tenant-attribute `leave.nonRejectableAutoApprove`. */
  | 'non-rejectable';

export interface AutoApproveWarning {
  routes: AutoApproveRoute[];
  /** true se il tipo si auto-approva **sempre**, quindi il workflow non si applica mai. */
  always: boolean;
  message: string;
}

/**
 * Avviso per l'editor del tipo di assenza: questo tipo si auto-approva, quindi il
 * workflow configurato non verrà applicato (o lo sarà solo a volte).
 *
 * `nonRejectableAutoApprove` è il tenant-attribute `leave.nonRejectableAutoApprove`
 * (true su tutti gli 8 tenant dev). Va passato dal chiamante: è configurazione di
 * tenant, non del tipo.
 */
export function autoApproveWarning(
  t: AutoApproveShape,
  nonRejectableAutoApprove: boolean,
): AutoApproveWarning | null {
  const routes: AutoApproveRoute[] = [];
  const policy = t.autoApprovePolicy ?? 'never';

  if (policy === 'always') routes.push('policy-always');
  else if (policy === 'with-document' || policy === 'with-substitute') routes.push('policy-conditional');
  if (t.isRejectable === false && nonRejectableAutoApprove) routes.push('non-rejectable');

  if (routes.length === 0) return null;

  const always = routes.includes('policy-always') || routes.includes('non-rejectable');
  const parts: string[] = [];
  if (routes.includes('policy-always')) parts.push('l’auto-approvazione è impostata su «sempre»');
  if (routes.includes('policy-conditional')) {
    parts.push(t.requiresDocument && t.requiresDocument !== 'none'
      ? 'si auto-approva quando il documento è presente'
      : 'si auto-approva quando la condizione dell’auto-approvazione è soddisfatta');
  }
  if (routes.includes('non-rejectable')) {
    parts.push('non è rifiutabile e l’azienda auto-approva i tipi non rifiutabili');
  }

  const head = always
    ? 'Questo tipo si auto-approva al momento dell’invio'
    : 'Questo tipo può auto-approvarsi al momento dell’invio';
  const tail = always
    ? 'Il workflow di approvazione non verrà applicato.'
    : 'Quando succede, il workflow di approvazione non viene applicato.';

  return { routes, always, message: `${head}: ${parts.join('; ')}. ${tail}` };
}
